import * as R from 'ramda';
import { all, put, call, fork, take, cancel, select, takeEvery, takeLatest } from 'redux-saga/effects';
// common
import { setSocketConnection } from '../../common/actions';
// components
import { closeModal } from '../../components/modal/actions';
import { pickerColorGrey } from '../../components/color-picker';
import { openLoader, closeLoader } from '../../components/loader/actions';
// features
import { messageType } from '../sockets/constants';
import * as C from '../centralized-dispatch/constants';
import { switchBranchRequest } from '../branch/actions';
import { makeSelectCurrentBranchGuid } from '../branch/selectors';
import { makeSelectOrderTable } from '../../common/idb/order/selectors';
import { getGeofencingZoneColorConfigRequest } from '../configurations/actions';
import { getDriverAvailabilityStatusRequest } from '../fleet-profile-simple/actions';
import {
  makeSelectDriversSearchCriteria,
  makeSelectDriversRadiusRequestParams,
} from '../centralized-dispatch/selectors';
import {
  socketPostMessage,
  receiveAvailableDriversStatusSocket,
  socketAvailableDriversStatusReceived,
  receiveAvailableDriversReservationSocket,
  socketAvailableDriversReservationReceived,
} from '../sockets/actions';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// sagas
import { visitPageSaga } from '../../sagas';
import { crudSaga } from '../../sagas/common';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// feature available-driver
import * as A from './actions';
import {
  selectDriverByGuid,
  makeSelectItemList,
  makeSelectActiveList,
  makeSelectSearchDate,
  makeSelectTruckTypes,
  makeSelectPagination,
  makeSelectPageVisited,
  makeSelectSearchRadius,
  makeSelectDriverInitials,
  makeSelectSearchLocation,
  makeSelectAllDriversList,
  makeSelectTitleSortValues,
  makeSelectTableTitleFilters,
  makeSelectPrevItemListGuids,
  makeSelectCentralizedDispatch,
  makeSelectShowUnassignedLoads,
} from './selectors';
//////////////////////////////////////////////////

export function* socketSubscribeOrUnsubscribeSaga({ payload }) {
  const { isSubscribe, driverGuids } = payload;

  const action = G.getPropFromObject(G.ifElse(isSubscribe, 'subscribe', 'unsubscribe'), messageType);

  yield all(R.map((driverGuid) => put(socketPostMessage({
    action,
    destination: `/driver/${driverGuid}/availableDrivers`,
  })), R.or(driverGuids, [])));
}

export function* subscribeToSocketSaga() {
  const list = yield select(makeSelectItemList());
  const allList = yield select(makeSelectAllDriversList());

  if (G.isAllNilOrEmpty([list, allList])) return;

  const driversGuids = R.keys(R.or(list, {}));
  const allDriversGuids = R.keys(R.or(allList, {}));

  let driversToSubscribe = G.ifElse(G.isNilOrEmpty(list), allDriversGuids, driversGuids);

  if (G.isAllNotNilOrNotEmpty([list, allList])) {
    driversToSubscribe = R.uniq(R.concat(driversGuids, allDriversGuids));
  }

  yield call(socketSubscribeOrUnsubscribeSaga, {
    payload: { isSubscribe: true, [GC.FIELD_DRIVER_GUIDS]: driversToSubscribe },
  });
}

export function* cancelSubscribeSaga() {
  try {
    const socketSubscribe = yield takeLatest(setSocketConnection, subscribeToSocketSaga);

    yield take(switchBranchRequest);

    yield cancel(socketSubscribe);
  } catch (error) {
    yield call(G.handleException, error, 'cancelSubscribeSaga exception');
  }
}

const colorStringValuePath = ['COLOR', GC.FIELD_STRING_VALUE];

export const filterTitleParamsByReportFields = (titleParams, uiReportFields) => {
  if (G.isNilOrEmpty(uiReportFields)) return titleParams;

  return R.omit(R.difference(R.keys(titleParams), uiReportFields), titleParams);
};

export const getOrderFields = (titleOrderFields, uiReportFields) => {
  if (G.isNilOrEmpty(titleOrderFields)) return [];

  return R.values(filterTitleParamsByReportFields(titleOrderFields, uiReportFields));
};

export const getSearchCriteria = (titleFilterParams, uiReportFields) => {
  if (G.isNilOrEmpty(titleFilterParams)) return [];

  let titleFilterParamsToUse = titleFilterParams;

  if (R.pathEq(pickerColorGrey, colorStringValuePath, titleFilterParams)) {
    titleFilterParamsToUse = R.assocPath(colorStringValuePath, null, titleFilterParamsToUse);
  }

  return R.values(filterTitleParamsByReportFields(
    titleFilterParamsToUse,
    G.isNotNilAndNotEmpty(uiReportFields)
      ? R.concat(uiReportFields, ['COLOR', 'LAST_KNOWN_LOCATION_COLOR', 'FUTURE_AVAILABILITY_LOCATION_COLOR'])
      : uiReportFields,
  ));
};

export function* handleGetItemListSaga({ payload }) {
  try {
    const withResetList = G.isTrue(payload);

    if (withResetList) yield put(openLoader({ showDimmer: true }));

    yield put(A.setListLoading(true));

    const searchDate = yield select(makeSelectSearchDate());
    const truckTypes = yield select(makeSelectTruckTypes());
    const pagination = yield select(makeSelectPagination());
    const searchRadius = yield select(makeSelectSearchRadius());
    const uiFieldsListTable = yield select(makeSelectOrderTable());
    const driverInitials = yield select(makeSelectDriverInitials());
    const searchLocation = yield select(makeSelectSearchLocation());
    const titleOrderFields = yield select(makeSelectTitleSortValues());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());
    const titleFilterParams = yield select(makeSelectTableTitleFilters());

    const uiReportFields = G.getPropFromObject(
      `${GC.AVAILABLE_DRIVERS_UI_REPORTS}AvailableDriversReport`,
      uiFieldsListTable,
    );

    const location = G.ifElse(
      G.isNilOrEmpty(searchLocation),
      null,
      {
        [GC.FIELD_LATITUDE]: G.getPropFromObject(GC.FIELD_LATITUDE, searchLocation),
        [GC.FIELD_LONGITUDE]: G.getPropFromObject(GC.FIELD_LONGITUDE, searchLocation),
      },
    );

    const { limit, offset } = pagination;

    const options = {
      data: {
        limit,
        offset,
        location,
        searchDate,
        truckTypes,
        driverInitials,
        radius: searchRadius,
        [GC.BRANCH_GUID]: currentBranchGuid,
        radiusUom: G.getMileOrKmUomByUomSystem(),
        orderFields: getOrderFields(titleOrderFields, uiReportFields),
        searchCriteria: getSearchCriteria(titleFilterParams, uiReportFields),
      },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.availableDriverList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const prevDriverListGuids = yield select(makeSelectPrevItemListGuids());

      const driverListGuids = R.map(R.prop(GC.FIELD_GUID), R.pathOr([], ['results'], data));

      if (withResetList) {
        const driversToUnsubscribe = R.difference(prevDriverListGuids, driverListGuids);

        yield call(socketSubscribeOrUnsubscribeSaga, { payload: { [GC.FIELD_DRIVER_GUIDS]: driversToUnsubscribe } });
      }

      const driversToSubscribe = withResetList
        ? R.difference(driverListGuids, prevDriverListGuids)
        : driverListGuids;

      yield call(socketSubscribeOrUnsubscribeSaga, {
        payload: { isSubscribe: true, [GC.FIELD_DRIVER_GUIDS]: driversToSubscribe },
      });

      yield put(A.getItemListSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetItemListSaga fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetItemListSaga exception');
  } finally {
    if (G.isTrue(payload)) yield put(closeLoader());

    yield put(A.setListLoading(false));
  }
}

export function* handleChangeActiveListSaga({ payload }) {
  try {
    yield put(A.setActiveList(payload));

    if (R.equals(payload, 'allDrivers')) {
      yield put(A.getAllDriversListRequest(true));
    } else {
      const itemList = yield select(makeSelectItemList());
      const allDriversList = yield select(makeSelectAllDriversList());

      yield call(socketSubscribeOrUnsubscribeSaga, {
        payload: { [GC.FIELD_DRIVER_GUIDS]: R.difference(R.keys(allDriversList), R.keys(itemList)) },
      });

      yield put(A.resetAllDriversListAndPagination(true));
    }
  } catch (err) {
    yield put(closeLoader());

    yield call(G.handleException, err, 'handleChangeActiveListSaga exception');
  }
}

export function* handleGetAllDriversListRequestSaga({ payload }) {
  const withResetList = R.or(
    G.isTrue(payload),
    R.pathEq(true, ['isInitial'], payload),
  );

  try {
    if (withResetList) yield put(openLoader({ showDimmer: true }));

    yield put(A.setAllDriversListLoading(true));

    const pagination = yield select(makeSelectPagination());
    const uiFieldsListTable = yield select(makeSelectOrderTable());
    const titleOrderFields = yield select(makeSelectTitleSortValues());
    const titleFilterParams = yield select(makeSelectTableTitleFilters());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());
    const payloadSearchCriteria = R.pathOr([], ['searchCriteria'], payload);
    const centralizedDispatch = yield select(makeSelectCentralizedDispatch());
    const storedDriversRadiusRequestParams = yield select(makeSelectDriversRadiusRequestParams());

    const driversRadiusRequestParams = G.ifElse(
      centralizedDispatch,
      storedDriversRadiusRequestParams,
      {},
    );

    const filtersFormSearchCriteria = G.ifElse(
      centralizedDispatch,
      yield select(makeSelectDriversSearchCriteria()),
      payloadSearchCriteria,
    );

    const uiReportFields = G.getPropFromObject(
      `${GC.AVAILABLE_DRIVERS_UI_REPORTS}AllDriversReport`,
      uiFieldsListTable,
    );

    const { limit, offset } = pagination;

    const titleSearchCriteria = getSearchCriteria(titleFilterParams, uiReportFields);

    const combinedSearchCriteria = R.concat(
      filtersFormSearchCriteria,
      titleSearchCriteria,
    );

    const hasDriversRadiusRequestParams = G.isAllNotNilOrNotEmpty([
      R.prop(GC.FIELD_RADIUS, driversRadiusRequestParams),
      R.prop(GC.FIELD_RADIUS_UOM, driversRadiusRequestParams),
      R.path([GC.FIELD_LOCATION, GC.FIELD_LATITUDE], driversRadiusRequestParams),
      R.path([GC.FIELD_LOCATION, GC.FIELD_LONGITUDE], driversRadiusRequestParams),
    ]);

    const searchCriteria = hasDriversRadiusRequestParams
      ? R.reject(
        ({ propertyName }) => R.includes(propertyName, [
          C.DRIVER_LAST_KNOWN_LOCATION,
          C.DRIVER_LAST_KNOWN_LOCATION_CITY,
          C.DRIVER_LAST_KNOWN_LOCATION_STATE,
        ]),
        combinedSearchCriteria,
      )
      : combinedSearchCriteria;

    const options = {
      data: {
        ...driversRadiusRequestParams,
        limit,
        offset,
        searchCriteria,
        [GC.FIELD_BRANCH_GUID]: currentBranchGuid,
        orderFields: getOrderFields(titleOrderFields, uiReportFields),
      },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.driverAvailabilityAllDriversList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const availableDriversItemList = yield select(makeSelectItemList());
      const prevDriverListGuids = yield select(makeSelectPrevItemListGuids());

      const availableDriversGuids = R.keys(availableDriversItemList);
      const driverListGuids = R.map(R.prop(GC.FIELD_GUID), R.pathOr([], ['results'], data));

      if (withResetList) {
        const driversToUnsubscribe = R.difference(
          prevDriverListGuids,
          R.concat(driverListGuids, availableDriversGuids),
        );

        yield call(socketSubscribeOrUnsubscribeSaga, { payload: { [GC.FIELD_DRIVER_GUIDS]: driversToUnsubscribe } });
      }

      const driversToSubscribe = withResetList
        ? R.difference(driverListGuids, R.concat(prevDriverListGuids, availableDriversGuids))
        : R.difference(driverListGuids, availableDriversGuids);

      yield call(socketSubscribeOrUnsubscribeSaga, {
        payload: { isSubscribe: true, [GC.FIELD_DRIVER_GUIDS]: driversToSubscribe },
      });

      yield put(A.getAllDriversListSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetAllDriversListRequestSaga fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleGetAllDriversListRequestSaga exception');
  } finally {
    yield put(A.setAllDriversListLoading(false));

    if (withResetList) yield put(closeLoader());
  }
}

export function* handleGetAvailableDriverByGuidRequestSaga({ payload }) {
  try {
    const { driverGuid } = payload;

    const searchRadius = yield select(makeSelectSearchRadius());
    const searchLocation = yield select(makeSelectSearchLocation());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const location = G.ifElse(
      G.isNilOrEmpty(searchLocation),
      null,
      {
        [GC.FIELD_LATITUDE]: G.getPropFromObject(GC.FIELD_LATITUDE, searchLocation),
        [GC.FIELD_LONGITUDE]: G.getPropFromObject(GC.FIELD_LONGITUDE, searchLocation),
      },
    );

    const options = {
      data: {
        location,
        radius: searchRadius,
        guids: R.of(Array, driverGuid),
        [GC.BRANCH_GUID]: currentBranchGuid,
        radiusUom: G.getMileOrKmUomByUomSystem(),
      },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.driverAvailabilityAllDriversList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getAvailableDriverByGuidSuccess({ driverGuid, data: R.head(G.getPropFromObject('results', data)) }));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetAvailableDriverByGuidRequestSaga fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleGetAvailableDriverByGuidRequestSaga exception');
  }
}

export function* handleCreateOrUpdateReservationRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const { isCreate } = payload;

    const reqData = R.omit(['isCreate'], payload);
    const method = G.ifElse(G.isTrue(isCreate), 'post', 'put');

    const res = yield call(sendRequest, method, endpointsMap.availableDriverReserve, { data: reqData });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeModal());

      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateOrUpdateReservationRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleCreateOrUpdateReservationRequestSaga exception');
  }
}

export function* handleDeleteReservationRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const res = yield call(sendRequest, 'post', endpointsMap.availableDriverDeleteReservation, { data: payload });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeModal());

      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      yield call(G.handleFailResponse, res, 'handleDeleteReservationRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleDeleteReservationRequestSaga exception');
  }
}

export function* handleCreateOrUpdateDriverNoteRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const { isCreate } = payload;

    const reqData = R.omit(['isCreate'], payload);
    const method = G.ifElse(G.isTrue(isCreate), 'post', 'put');

    const res = yield call(sendRequest, method, endpointsMap.availableDriverNote, { data: reqData });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateOrUpdateDriverNoteRequestSaga fail');
    }

    yield put(closeModal());
    yield put(closeLoader());
  } catch (error) {
    yield put(closeModal());
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleCreateOrUpdateDriverNoteRequestSaga exception');
  }
}

export function* handleDeleteDriverNoteRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const options = {
      params: {
        [GC.FIELD_GUID]: payload,
      },
    };

    const res = yield call(sendRequest, 'delete', endpointsMap.availableDriverNote, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:204');
    } else {
      yield call(G.handleFailResponse, res, 'handleDeleteDriverNoteRequestSaga fail');
    }

    yield put(closeModal());
    yield put(closeLoader());
  } catch (error) {
    yield put(closeModal());
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleDeleteDriverNoteRequestSaga exception');
  }
}

export function* handleGetDriverUnavailablePeriodListRequestSaga(options) {
  try {
    const res = yield call(sendRequest, 'get', endpointsMap.routeFleetDriverUnavailablePeriodList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) return data;

    yield call(G.handleFailResponse, res, 'handleGetDriverUnavailablePeriodRequestSaga fail');
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleGetDriverUnavailablePeriodRequestSaga exception');
  }
}

export function* handleGetDriverPendingActivityListRequestSaga(options) {
  try {
    const res = yield call(sendRequest, 'get', endpointsMap.routeFleetDriverPendingActivityList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) return data;

    yield call(G.handleFailResponse, res, 'handleGetDriverPendingActivityListRequestSaga fail');
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleGetDriverPendingActivityListRequestSaga exception');
  }
}

export function* handleGetDriverDataRequestSaga({ payload }) {
  try {
    const { callback, driverGuid } = payload;

    const options = { params: { driverGuid } };

    const [pendingActivities = [], unavailablePeriods = []] = yield all([
      call(handleGetDriverPendingActivityListRequestSaga, options),
      call(handleGetDriverUnavailablePeriodListRequestSaga, options),
    ]);

    G.callFunctionWithArgs(callback, { pendingActivities, unavailablePeriods });
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleGetDriverDataRequestSaga exception');
  }
}

export function* handleSetDriverUnavailableRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const res = yield call(sendRequest, 'put', endpointsMap.driverAvailabilityUnavailable, { params: payload });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');

      yield put(getDriverAvailabilityStatusRequest(G.getPropFromObject(GC.FIELD_DRIVER_GUID, payload)));
    } else {
      yield call(G.handleFailResponse, res, 'handleSetDriverUnavailableRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleSetDriverUnavailableRequestSaga exception');
  }
}

export function* handleSetDriverAvailableRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const { location, driverGuid, availableDate } = payload;

    const options = {
      params: { driverGuid },
      data: { location, availableDate },
    };

    const res = yield call(sendRequest, 'put', endpointsMap.driverAvailabilityAvailable, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');

      yield put(getDriverAvailabilityStatusRequest(driverGuid));
      yield put(closeModal());
    } else {
      yield call(G.handleFailResponse, res, 'handleSetDriverAvailableRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeModal());
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleSetDriverAvailableRequestSaga exception');
  }
}

export function* handleGetSearchLocationRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: {
        templateId: payload,
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.location, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const { zip, city, state, country, address1, latitude, longitude } = data;

      const location = `${address1}, ${city}, ${state}, ${country}, ${zip}`;

      yield put(A.setLocationValue(location));
      yield put(A.setSearchLocation({ latitude, longitude }));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetSearchLocationRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'handleGetSearchLocationRequestSaga exception');
  }
}

export function* handleGetUnassignedLoadsRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const options = {
      data: R.mergeRight(payload, {
        offset: 0,
        limit: 200,
        radiusUom: G.getMileOrKmUomByUomSystem(),
      }),
    };

    yield call(crudSaga, {
      options,
      method: 'post',
      endpoint: endpointsMap.unassignedLoads,
      successAction: A.getUnassignedLoadsSuccess,
      parentSagaName: 'getUnassignedLoadsRequest',
    });

    yield put(closeLoader());
  } catch (error) {
    yield call(G.handleException, error, 'getUnassignedLoadsRequest exception');
  }
}

export function* handleAssignDriverOnAvailableDriverRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const omittedPayload = R.omit(GC.GROUPED_FIELDS.FLEET_ASSIGNMENT_PICK_ARR, payload);
    const fleetAssignment = R.pick(GC.GROUPED_FIELDS.FLEET_ASSIGNMENT_PICK_ARR, payload);

    const options = {
      data: R.assoc(GC.FIELD_FLEET_ASSIGNMENT, fleetAssignment, omittedPayload),
    };

    const res = yield call(sendRequest, 'post', endpointsMap.telFleetRate, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeModal());
      yield put(A.assignDriverOnAvailableDriverSuccess(payload));

      yield call(G.showToastrMessage, 'success', 'messages:success:create');
    } else {
      yield call(G.handleFailResponse, res, 'handleAssignDriverOnAvailableDriverRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeModal());
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleAssignDriverOnAvailableDriverRequestSaga exception');
  }
}

export function* handleUpdateAvailableDriversColorRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const { guid, color } = payload;

    const options = { data: { guid, color: G.ifElse(R.equals(color, pickerColorGrey), null, color) } };

    const res = yield call(sendRequest, 'put', endpointsMap.availableDriversColor, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      yield call(G.handleFailResponse, res, 'handleUpdateAvailableDriversColorRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleUpdateAvailableDriversColorRequestSaga exception');
  }
}

export function* handlePatchUpdateAvailableDriverRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const { data, guid } = payload;

    const res = yield call(sendRequest, 'patch', endpointsMap.getDriverAvailabilityEndpoint(guid), { data });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      yield call(G.handleFailResponse, res, 'handlePatchUpdateAvailableDriverRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handlePatchUpdateAvailableDriverRequestSaga exception');
  }
}

export const hasDriverTrip = (driver) => G.isOneNotNilOrNotEmpty(R.props([GC.FIELD_TEL, GC.FIELD_NEXT_TEL], driver));

export function* handleReceiveAvailableDriversStatusSocketSaga({ payload }) {
  try {
    const { driverGuid } = payload;

    const driver = yield select(selectDriverByGuid, driverGuid);

    const hasTrip = hasDriverTrip(driver);

    if (hasTrip) {
      yield put(A.getAvailableDriverByGuidRequest(payload));

      return;
    }

    yield put(socketAvailableDriversStatusReceived(payload));
  } catch (error) {
    yield call(G.handleException, error, 'handleReceiveAvailableDriversStatusSocketSaga exception');
  }
}

export function* handleReceiveAvailableDriversReservationSocketSaga({ payload }) {
  try {
    const { driverGuid, entityOperation } = payload;

    if (R.equals(entityOperation, 'DELETE')) {
      const driver = yield select(selectDriverByGuid, driverGuid);

      const hasTrip = hasDriverTrip(driver);

      if (hasTrip) {
        yield put(A.getAvailableDriverByGuidRequest(payload));

        return;
      }
    }

    yield put(socketAvailableDriversReservationReceived(payload));
  } catch (error) {
    yield call(G.handleException, error, 'handleReceiveAvailableDriversReservationSocketSaga exception');
  }
}

export function* handleVisitAvailableDriverListSaga({ payload }) {
  while (true) { // eslint-disable-line
    yield call(visitPageSaga, payload, GC.CHECK_VISIT_ROUTE_AVAILABLE_DRIVER_PAGE);

    try {
      const pageVisited = yield select(makeSelectPageVisited());
      const centralizedDispatch = R.propOr(false, 'centralizedDispatch', payload);
      const wasCentralizedDispatch = yield select(makeSelectCentralizedDispatch());
      const shouldInitialize = R.or(
        G.isFalse(pageVisited),
        R.or(centralizedDispatch, wasCentralizedDispatch),
      );

      yield put(openLoader({ showDimmer: true }));

      if (shouldInitialize) {
        yield put(A.setInitialState(centralizedDispatch));

        const activeList = yield select(makeSelectActiveList());

        if (R.equals(activeList, 'allDrivers')) {
          const listRequestPayload = G.ifElse(
            centralizedDispatch,
            {
              isInitial: true,
              centralizedDispatch: true,
              searchCriteria: R.propOr([], 'searchCriteria', payload),
            },
            true,
          );

          yield put(A.getAllDriversListRequest(listRequestPayload));
        }

        const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

        yield put(getGeofencingZoneColorConfigRequest(currentBranchGuid));
      } else {
        const showUnassignedLoads = yield select(makeSelectShowUnassignedLoads());

        if (G.isTrue(showUnassignedLoads)) yield put(A.toggleUnassignedLoads());
      }

      yield fork(cancelSubscribeSaga);
    } finally {
      yield put(closeLoader());
    }

    break;
  }
}

export function* availableDriverWatcherSaga() {
  yield takeLatest(A.getItemListRequest, handleGetItemListSaga);
  yield takeLatest(A.changeActiveList, handleChangeActiveListSaga);
  yield takeLatest(A.getDriverDataRequest, handleGetDriverDataRequestSaga);
  yield takeLatest(A.deleteDriverNoteRequest, handleDeleteDriverNoteRequestSaga);
  yield takeLatest(A.getAllDriversListRequest, handleGetAllDriversListRequestSaga);
  yield takeLatest(A.getSearchLocationRequest, handleGetSearchLocationRequestSaga);
  yield takeLatest(A.deleteReservationRequest, handleDeleteReservationRequestSaga);
  yield takeLatest(A.setDriverAvailableRequest, handleSetDriverAvailableRequestSaga);
  yield takeLatest(A.getUnassignedLoadsRequest, handleGetUnassignedLoadsRequestSaga);
  yield takeLatest(A.setDriverUnavailableRequest, handleSetDriverUnavailableRequestSaga);
  yield takeLatest(GC.VISIT_ROUTE_AVAILABLE_DRIVER_PAGE, handleVisitAvailableDriverListSaga);
  yield takeEvery(A.getAvailableDriverByGuidRequest, handleGetAvailableDriverByGuidRequestSaga);
  yield takeLatest(A.createOrUpdateDriverNoteRequest, handleCreateOrUpdateDriverNoteRequestSaga);
  yield takeLatest(A.createOrUpdateReservationRequest, handleCreateOrUpdateReservationRequestSaga);
  yield takeLatest(A.patchUpdateAvailableDriverRequest, handlePatchUpdateAvailableDriverRequestSaga);
  yield takeLatest(A.updateAvailableDriversColorRequest, handleUpdateAvailableDriversColorRequestSaga);
  yield takeLatest(receiveAvailableDriversStatusSocket, handleReceiveAvailableDriversStatusSocketSaga);
  yield takeLatest(A.assignDriverOnAvailableDriverRequest, handleAssignDriverOnAvailableDriverRequestSaga);
  yield takeLatest(receiveAvailableDriversReservationSocket, handleReceiveAvailableDriversReservationSocketSaga);
}

export default availableDriverWatcherSaga;
